import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  getLanguage,
  isAppLanguage,
  normalizeLanguage,
  SUPPORTED_LANGUAGES,
  type AppLanguage,
} from '../../i18n/languages';
import { setAppLanguage } from '../../i18n';

interface LanguageSwitcherProps {
  className?: string;
}

export function LanguageSwitcher({ className = '' }: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const currentCode =
    (isAppLanguage(i18n.resolvedLanguage) && i18n.resolvedLanguage) ||
    normalizeLanguage(i18n.language) ||
    'ru';
  const current = getLanguage(currentCode);

  useEffect(() => {
    if (!open) return;

    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const selectLanguage = async (code: AppLanguage) => {
    await setAppLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={`cs-language-switcher ${className}`.trim()}>
      <button
        type="button"
        className="cs-language-switcher__trigger"
        aria-label={t('settings.chooseLanguage')}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="cs-language-switcher__flag" aria-hidden>
          {current.flag}
        </span>
        <span className="cs-language-switcher__code">{current.code.toUpperCase()}</span>
        <span className={`cs-language-switcher__chevron${open ? ' cs-language-switcher__chevron--open' : ''}`} aria-hidden>
          ▾
        </span>
      </button>

      {open &&
        createPortal(
          <div className="cs-language-switcher__backdrop" onClick={() => setOpen(false)} aria-hidden />,
          document.body,
        )}

      {open && (
        <div className="cs-language-switcher__menu" role="listbox" aria-label={t('settings.chooseLanguage')}>
          <div className="cs-language-switcher__title">{t('settings.chooseLanguage')}</div>
          {SUPPORTED_LANGUAGES.map((language) => {
            const active = language.code === currentCode;
            return (
              <button
                key={language.code}
                type="button"
                role="option"
                aria-selected={active}
                className={`cs-language-switcher__option${active ? ' cs-language-switcher__option--active' : ''}`}
                onClick={() => void selectLanguage(language.code)}
              >
                <span className="cs-language-switcher__option-flag" aria-hidden>
                  {language.flag}
                </span>
                <span className="cs-language-switcher__option-copy">
                  <strong>{language.nativeLabel}</strong>
                  <small>{language.label}</small>
                </span>
                {active && <span className="cs-language-switcher__check" aria-hidden>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
